/**
 * QuotaEditor Component (Refactored)
 *
 * Editor for quota limits with nested structure:
 * - concurrency: Concurrent request limit
 * - token: Token quota with per_minute/per_hour/per_day/per_month/total
 * - request: Request quota with per_minute/per_hour/per_day/per_month/total
 */

import React, { useState, useCallback } from 'react';
import { clsx } from 'clsx';

export interface TokenQuota {
  per_minute?: number | false;
  per_hour?: number | false;
  per_day?: number | false;
  per_month?: number | false;
  total?: number | false;
}

export interface RequestQuota {
  per_minute?: number | false;
  per_hour?: number | false;
  per_day?: number | false;
  per_month?: number | false;
  total?: number | false;
}

export interface QuotaLimits {
  concurrency?: number | false;
  token?: TokenQuota;
  request?: RequestQuota;
}

export interface QuotaEditorProps {
  value: QuotaLimits;
  onChange: (value: QuotaLimits) => void;
  disabled?: boolean;
  compact?: boolean;
  defaultCollapsed?: boolean;
  className?: string;
}

interface QuotaFieldConfig {
  key: string;
  label: string;
  placeholder: string;
}

const TOKEN_FIELDS: QuotaFieldConfig[] = [
  { key: 'per_minute', label: 'Token/分钟', placeholder: '例如: 100000' },
  { key: 'per_hour', label: 'Token/小时', placeholder: '例如: 1000000' },
  { key: 'per_day', label: 'Token/天', placeholder: '例如: 10000000' },
  { key: 'per_month', label: 'Token/月', placeholder: '例如: 100000000' },
  { key: 'total', label: 'Token总量', placeholder: '例如: 1000000000' },
];

const REQUEST_FIELDS: QuotaFieldConfig[] = [
  { key: 'per_minute', label: '请求/分钟', placeholder: '例如: 60' },
  { key: 'per_hour', label: '请求/小时', placeholder: '例如: 1000' },
  { key: 'per_day', label: '请求/天', placeholder: '例如: 10000' },
  { key: 'per_month', label: '请求/月', placeholder: '例如: 200000' },
  { key: 'total', label: '请求总量', placeholder: '例如: 1000000' },
];

export const QuotaEditor: React.FC<QuotaEditorProps> = ({
  value,
  onChange,
  disabled = false,
  compact = false,
  defaultCollapsed = false,
  className = '',
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(compact || defaultCollapsed ? [] : ['concurrency', 'token', 'request'])
  );

  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  }, []);

  const handleConcurrencyChange = useCallback(
    (newValue: string) => {
      if (disabled) return;
      const numValue = newValue === '' ? undefined : parseInt(newValue, 10);
      if (numValue !== undefined && isNaN(numValue)) return;
      onChange({
        ...value,
        concurrency: numValue === undefined ? false : numValue,
      });
    },
    [value, onChange, disabled]
  );

  const handleConcurrencyToggle = useCallback(() => {
    if (disabled) return;
    const current = value.concurrency;
    onChange({
      ...value,
      concurrency: current === false ? 1 : false,
    });
  }, [value, onChange, disabled]);

  const handleTokenFieldChange = useCallback(
    (key: string, newValue: string) => {
      if (disabled) return;
      const numValue = newValue === '' ? undefined : parseInt(newValue, 10);
      if (numValue !== undefined && isNaN(numValue)) return;
      const currentToken = value.token || {};
      onChange({
        ...value,
        token: {
          ...currentToken,
          [key]: numValue === undefined ? false : numValue,
        },
      });
    },
    [value, onChange, disabled]
  );

  const handleTokenFieldToggle = useCallback(
    (key: string) => {
      if (disabled) return;
      const currentToken = value.token || {};
      const currentValue = currentToken[key as keyof TokenQuota];
      onChange({
        ...value,
        token: {
          ...currentToken,
          [key]: currentValue === false ? 100 : false,
        },
      });
    },
    [value, onChange, disabled]
  );

  const handleRequestFieldChange = useCallback(
    (key: string, newValue: string) => {
      if (disabled) return;
      const numValue = newValue === '' ? undefined : parseInt(newValue, 10);
      if (numValue !== undefined && isNaN(numValue)) return;
      const currentRequest = value.request || {};
      onChange({
        ...value,
        request: {
          ...currentRequest,
          [key]: numValue === undefined ? false : numValue,
        },
      });
    },
    [value, onChange, disabled]
  );

  const handleRequestFieldToggle = useCallback(
    (key: string) => {
      if (disabled) return;
      const currentRequest = value.request || {};
      const currentValue = currentRequest[key as keyof RequestQuota];
      onChange({
        ...value,
        request: {
          ...currentRequest,
          [key]: currentValue === false ? 100 : false,
        },
      });
    },
    [value, onChange, disabled]
  );

  const renderConcurrencySection = () => {
    const concurrencyValue = value.concurrency;
    const isEnabled = concurrencyValue !== false && concurrencyValue !== undefined;
    const displayValue: number = isEnabled && typeof concurrencyValue === 'number' ? concurrencyValue : 1;
    const isExpanded = expandedSections.has('concurrency');

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('concurrency')}
          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div>
            <h4 className="text-sm font-medium text-gray-800">并发限制</h4>
            <p className="text-xs text-gray-500">同时进行的请求数量限制</p>
          </div>
          <div className="flex items-center gap-2">
            {isEnabled && (
              <span className="text-xs text-gray-600">当前: {displayValue}</span>
            )}
            <svg
              className={clsx(
                'w-5 h-5 text-gray-500 transition-transform',
                isExpanded && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>
        {isExpanded && (
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer min-w-[120px]">
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={handleConcurrencyToggle}
                  disabled={disabled}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">启用</span>
              </label>
              <input
                type="number"
                value={displayValue}
                onChange={(e) => handleConcurrencyChange(e.target.value)}
                disabled={disabled || !isEnabled}
                min={1}
                placeholder="例如: 5"
                className={clsx(
                  'flex-1 px-3 py-1.5 border rounded-lg text-sm transition-colors',
                  isEnabled
                    ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    : 'border-gray-200 bg-gray-50 text-gray-400',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
              />
              <span className="text-xs text-gray-500">
                {!isEnabled && '无限制'}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderQuotaGroupSection = (
    sectionKey: string,
    title: string,
    description: string,
    fields: QuotaFieldConfig[],
    quotaData: TokenQuota | RequestQuota | undefined,
    onFieldChange: (key: string, value: string) => void,
    onFieldToggle: (key: string) => void
  ) => {
    const isExpanded = expandedSections.has(sectionKey);
    const enabledCount = fields.filter(
      (f) => quotaData && quotaData[f.key as keyof typeof quotaData] !== false && quotaData[f.key as keyof typeof quotaData] !== undefined
    ).length;

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection(sectionKey)}
          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div>
            <h4 className="text-sm font-medium text-gray-800">{title}</h4>
            <p className="text-xs text-gray-500">{description}</p>
          </div>
          <div className="flex items-center gap-2">
            {enabledCount > 0 && (
              <span className="text-xs text-gray-600">{enabledCount} 个限制</span>
            )}
            <svg
              className={clsx(
                'w-5 h-5 text-gray-500 transition-transform',
                isExpanded && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>
        {isExpanded && (
          <div className="p-4 bg-white border-t border-gray-200">
            {fields.map((field) => {
              const fieldKey = field.key as keyof typeof quotaData;
              const isEnabled = quotaData && quotaData[fieldKey] !== false && quotaData[fieldKey] !== undefined;
              const displayValue = isEnabled ? quotaData[fieldKey] : 100;

              return (
                <div key={field.key} className="flex items-center gap-3 py-2">
                  <label className="flex items-center gap-2 cursor-pointer min-w-[140px]">
                    <input
                      type="checkbox"
                      checked={isEnabled || false}
                      onChange={() => onFieldToggle(field.key)}
                      disabled={disabled}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {field.label}
                    </span>
                  </label>
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      type="number"
                      value={displayValue}
                      onChange={(e) => onFieldChange(field.key, e.target.value)}
                      disabled={disabled || !isEnabled}
                      min={0}
                      placeholder={field.placeholder}
                      className={clsx(
                        'flex-1 px-3 py-1.5 border rounded-lg text-sm transition-colors',
                        isEnabled
                          ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                          : 'border-gray-200 bg-gray-50 text-gray-400',
                        disabled && 'cursor-not-allowed opacity-50'
                      )}
                    />
                    <span className="text-xs text-gray-500 w-8">
                      {!isEnabled && '无限制'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={clsx('space-y-3', className)}>
      {!compact && (
        <div className="text-xs text-gray-500 mb-2">
          勾选启用配额限制，取消勾选表示无限制
        </div>
      )}
      {renderConcurrencySection()}
      {renderQuotaGroupSection(
        'token',
        'Token 配额',
        '按时间窗口限制 Token 使用量',
        TOKEN_FIELDS,
        value.token,
        handleTokenFieldChange,
        handleTokenFieldToggle
      )}
      {renderQuotaGroupSection(
        'request',
        '请求配额',
        '按时间窗口限制请求次数',
        REQUEST_FIELDS,
        value.request,
        handleRequestFieldChange,
        handleRequestFieldToggle
      )}
    </div>
  );
};

export default QuotaEditor;